import React, { useEffect, useState } from "react";
import { Loader2, Hammer, Crown } from "lucide-react";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const FurniCard = ({ it }) => (
  <div
    data-testid={`sammlung-furni-${it.classname}`}
    className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-[#10141b] p-4 transition-colors hover:border-[#7c4dff]/40"
    title={it.classname}
  >
    <div className="flex h-20 w-20 items-center justify-center">
      <img src={it.icon} alt={it.classname} className="hb-pixel-img max-h-20 max-w-20" loading="lazy" />
    </div>
    <div className="line-clamp-2 text-center text-sm font-semibold text-white">{it.name || it.classname}</div>
    {it.description && (
      <div className="line-clamp-3 text-center text-[11px] text-zinc-400">{it.description}</div>
    )}
    <div className="mt-1 truncate text-[9px] font-mono uppercase tracking-wider text-[#9d7bff] opacity-70">{it.classname}</div>
  </div>
);

const RareCard = ({ r }) => (
  <div
    data-testid={`sammlung-rare-${r.id}`}
    className="flex gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4"
  >
    {r.image && <img src={r.image} alt={r.name} className="hb-pixel-img h-16 w-16 flex-shrink-0" loading="lazy" />}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-300">
        <Crown className="h-2.5 w-2.5" /> {r.category || "Sammler"}
      </div>
      <div className="mt-0.5 truncate text-sm font-semibold text-white">{r.name}</div>
      {r.year && <div className="text-[11px] text-zinc-500">{r.year}</div>}
      {r.description && <div className="mt-1 line-clamp-2 text-[11px] text-zinc-400">{r.description}</div>}
    </div>
  </div>
);

const Sammlung = () => {
  const [tab, setTab] = useState("furni"); // furni | rares
  const [furni, setFurni] = useState([]);
  const [rares, setRares] = useState([]);
  const [loading, setLoading] = useState(true);
  const pd = usePageDescription("sammlung", {
    title: "Möbel & Rares",
    intro: "Die 20 neuesten Habbo-Möbel direkt aus den offiziellen Gamedata-Texten und die wichtigsten Rares aus 20+ Jahren Habbo-Geschichte.",
  });

  useEffect(() => {
    (async () => {
      try {
        const [f, r] = await Promise.all([
          api.latestFurniture(20),
          api.listRares(),
        ]);
        setFurni(f.items || []);
        setRares(r || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-4 py-10" data-testid="sammlung-page">
      <PageHero eyebrow={pd.eyebrow || "Habbo-Sammlung"} icon={Hammer} title={pd.title} keywords={pd.keywords} intro={pd.intro} />

      <div className="mt-6 inline-flex rounded-xl border border-white/10 bg-[#0f141b] p-1" role="tablist">
        <button
          data-testid="sammlung-tab-furni"
          onClick={() => setTab("furni")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "furni" ? "bg-[#7c4dff]/20 text-white" : "text-zinc-400 hover:text-white"}`}
        >Neueste Möbel</button>
        <button
          data-testid="sammlung-tab-rares"
          onClick={() => setTab("rares")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "rares" ? "bg-amber-500/20 text-amber-200" : "text-zinc-400 hover:text-white"}`}
        >Rares ({rares.length})</button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : tab === "furni" ? (
          furni.length === 0 ? (
            <div className="rounded-xl border border-white/5 bg-[#10141b] p-12 text-center text-sm text-zinc-500">
              Aktuell keine neuen Möbel mit offizieller Gamedata verfügbar.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {furni.map((it) => <FurniCard key={it.classname} it={it} />)}
            </div>
          )
        ) : (
          rares.length === 0 ? (
            <div className="rounded-xl border border-white/5 bg-[#10141b] p-12 text-center text-sm text-zinc-500">
              Noch keine Rares in der Sammlung.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {rares.map((r) => <RareCard key={r.id} r={r} />)}
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default Sammlung;
